import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, Gift, TrendingUp, Headphones, MapPin, Zap,
  Check, Phone, MessageCircle, ArrowRight, Mail,
  ClipboardList, UserCheck, Link2, Truck, Building2,
  Star, Quote, Upload, FileText, Plus, Minus, X, Briefcase,
} from "lucide-react";
import { LOGO, CONTACT, languages, t } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.pexels.com/photos/20097991/pexels-photo-20097991.jpeg";
const REQ_IMG = "https://images.pexels.com/photos/7019376/pexels-photo-7019376.jpeg";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Landing({ lang, setLang }) {
  const tr = t[lang];
  const [form, setForm] = useState({
    name: "", phone: "", email: "", license_type: "", experience: "", certificates: [], availability: "", message: "", cv_path: "", cv_filename: "",
  });
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [cookieOk, setCookieOk] = useState(() => localStorage.getItem("nl_cookie") === "1");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cv_path: res.data.path, cv_filename: res.data.filename }));
      toast.success("CV geüpload");
    } catch {
      toast.error("Upload mislukt");
    } finally {
      setUploading(false);
    }
  };

  const toggleCert = (c) =>
    setForm((f) => ({
      ...f,
      certificates: f.certificates.includes(c)
        ? f.certificates.filter((x) => x !== c)
        : [...f.certificates, c],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/applications`, form);
      toast.success(tr.f_success);
      setForm({ name: "", phone: "", email: "", license_type: "", experience: "", certificates: [], availability: "", message: "", cv_path: "", cv_filename: "" });
    } catch (err) {
      toast.error(tr.f_error);
    } finally {
      setSending(false);
    }
  };

  const benefits = [
    { icon: Users, t: tr.b1_t, d: tr.b1_d, big: true },
    { icon: Gift, t: tr.b2_t, d: tr.b2_d },
    { icon: TrendingUp, t: tr.b3_t, d: tr.b3_d },
    { icon: Headphones, t: tr.b4_t, d: tr.b4_d },
    { icon: MapPin, t: tr.b5_t, d: tr.b5_d },
    { icon: Zap, t: tr.b6_t, d: tr.b6_d, big: true },
  ];

  const stepIcons = [ClipboardList, UserCheck, Link2, Truck];

  const initials = (name) => name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const inputCls = "w-full bg-background border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <img src={LOGO} alt="Northline Recruitment" className="h-14 w-auto" data-testid="nav-logo" />
          <div className="hidden md:flex items-center gap-8 text-xs uppercase tracking-widest">
            <button onClick={() => scrollTo("benefits")} className="hover:text-primary transition-colors" data-testid="nav-benefits-link">{tr.nav_benefits}</button>
            <button onClick={() => scrollTo("process")} className="hover:text-primary transition-colors" data-testid="nav-process-link">{tr.nav_process}</button>
            <button onClick={() => scrollTo("requirements")} className="hover:text-primary transition-colors" data-testid="nav-requirements-link">{tr.nav_requirements}</button>
            <button onClick={() => scrollTo("reviews")} className="hover:text-primary transition-colors" data-testid="nav-reviews-link">{tr.nav_reviews}</button>
            <button onClick={() => scrollTo("employers")} className="hover:text-primary transition-colors" data-testid="nav-employers-link">{tr.nav_employers}</button>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-xs tracking-widest">
              {languages.map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  data-testid={`lang-${l.toLowerCase()}`}
                  className={`px-1.5 py-0.5 transition-colors ${lang === l ? "text-primary font-bold" : "text-muted-foreground hover:text-foreground"}`}
                >{l}</button>
              ))}
            </div>
            <button onClick={() => scrollTo("apply")} className="hidden sm:inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors" data-testid="nav-apply-btn">
              {tr.nav_apply}
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-[#08281d]">
        <div className="absolute inset-0">
          <video
            className="w-full h-full object-cover opacity-50"
            src={`${process.env.PUBLIC_URL}/hero.mp4`}
            poster={HERO_IMG}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            data-testid="hero-video"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#052017]/85 via-[#08281d]/85 to-[#0d3b2b]/60" />
          <div className="absolute inset-0 grain opacity-25" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full py-20 text-white">
          <motion.div initial="hidden" animate="show" variants={fade} className="max-w-3xl">
            <span className="inline-block text-[#5fe6a0] text-xs md:text-sm tracking-[0.25em] border border-[#5fe6a0]/40 px-4 py-2 mb-8">{tr.hero_tag}</span>
            <h1 className="font-anton text-6xl sm:text-7xl lg:text-8xl leading-[0.9] uppercase">
              {tr.hero_title_1}<br /><span className="text-[#3ee08a]">{tr.hero_title_2}</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-white/75 max-w-xl leading-relaxed">{tr.hero_sub}</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button onClick={() => scrollTo("apply")} className="group inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors" data-testid="hero-apply-btn">
                {tr.hero_cta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center justify-center gap-3 border border-white/30 px-8 py-4 text-sm font-bold uppercase tracking-widest hover:border-[#3ee08a] hover:text-[#3ee08a] transition-colors" data-testid="hero-call-btn">
                <Phone className="w-4 h-4" /> {tr.hero_cta2}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-border bg-card">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
          {[[tr.stat_1, tr.stat_1l], [tr.stat_2, tr.stat_2l], [tr.stat_3, tr.stat_3l]].map(([v, l], i) => (
            <div key={i} className="px-6 md:px-10 py-10 text-center sm:text-left">
              <div className="font-anton text-5xl md:text-6xl text-primary">{v}</div>
              <div className="mt-2 text-xs uppercase tracking-widest text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
            <h2 className="font-anton text-5xl md:text-6xl uppercase">{tr.benefits_title}</h2>
            <p className="mt-4 text-muted-foreground max-w-xl">{tr.benefits_sub}</p>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 border-t border-l border-border">
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fade}
                className={`group relative border-r border-b border-border p-8 md:p-10 hover:bg-card transition-colors ${b.big ? "md:col-span-1" : ""}`}
                data-testid={`benefit-${i}`}
              >
                <b.icon className="w-9 h-9 text-primary" strokeWidth={1.5} />
                <h3 className="mt-6 font-anton text-2xl uppercase">{b.t}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{b.d}</p>
                <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary group-hover:w-full transition-all duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="py-24 md:py-32 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
            <span className="inline-block text-primary text-xs tracking-[0.25em] border border-primary/30 px-4 py-2 mb-6">{tr.process_tag}</span>
            <h2 className="font-anton text-5xl md:text-6xl uppercase">{tr.process_title}</h2>
            <p className="mt-4 text-muted-foreground max-w-xl">{tr.process_sub}</p>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-px bg-border border border-border">
            {tr.steps.map((s, i) => {
              const Icon = stepIcons[i];
              return (
                <motion.div
                  key={i}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  variants={fade}
                  className="group relative bg-background p-8 md:p-10 hover:bg-card transition-colors"
                  data-testid={`step-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-9 h-9 text-primary" strokeWidth={1.5} />
                    <span className="font-anton text-5xl text-border group-hover:text-primary/30 transition-colors">0{i + 1}</span>
                  </div>
                  <h3 className="mt-6 font-anton text-2xl uppercase">{s.t}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REQUIREMENTS */}
      <section id="requirements" className="relative py-24 md:py-32 overflow-hidden bg-[#08281d] text-white">
        <div className="absolute inset-0">
          <img src={REQ_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#062319]/95 to-[#0d3b2b]/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 items-start">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
            <h2 className="font-anton text-5xl md:text-6xl uppercase">{tr.req_title}</h2>
            <p className="mt-4 text-white/70">{tr.req_sub}</p>
            <button onClick={() => scrollTo("apply")} className="mt-8 inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors" data-testid="req-apply-btn">
              {tr.nav_apply} <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
          <div className="border-t border-white/15">
            {tr.req.map((r, i) => (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fade}
                className="flex items-center gap-4 border-b border-white/15 py-5"
                data-testid={`req-${i}`}
              >
                <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-[#3ee08a]/20 text-[#5fe6a0]">
                  <Check className="w-4 h-4" />
                </span>
                <span className="text-sm md:text-base">{r}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VACANCIES */}
      <section id="vacancies" className="py-24 md:py-32 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
            <span className="inline-block text-primary text-xs tracking-[0.25em] border border-primary/30 px-4 py-2 mb-6">{tr.vac_tag}</span>
            <h2 className="font-anton text-5xl md:text-6xl uppercase">{tr.vac_title}</h2>
            <p className="mt-4 text-muted-foreground max-w-xl">{tr.vac_sub}</p>
          </motion.div>
          <div className="mt-12 border-t border-l border-border grid grid-cols-1 md:grid-cols-2">
            {tr.vacancies.map((v, i) => (
              <motion.button
                key={i}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fade}
                onClick={() => scrollTo("apply")}
                className="group text-left border-r border-b border-border p-8 bg-background hover:bg-card transition-colors flex items-start justify-between gap-4"
                data-testid={`vacancy-${i}`}
              >
                <div>
                  <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest"><Briefcase className="w-4 h-4" />{v.type}</div>
                  <h3 className="mt-3 font-anton text-2xl uppercase leading-tight">{v.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground flex items-center gap-1.5"><MapPin className="w-4 h-4" />{v.location}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="py-24 md:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <span className="inline-block text-primary text-xs tracking-[0.25em] border border-primary/30 px-4 py-2 mb-6">{tr.reviews_tag}</span>
              <h2 className="font-anton text-5xl md:text-6xl uppercase">{tr.reviews_title}</h2>
              <p className="mt-4 text-muted-foreground max-w-xl">{tr.reviews_sub}</p>
            </div>
            <div className="flex items-center gap-4 border border-border bg-card px-6 py-5" data-testid="reviews-rating">
              <span className="font-anton text-5xl text-primary leading-none">4.8</span>
              <div>
                <div className="flex gap-0.5">
                  {[0, 1, 2, 3, 4].map((s) => <Star key={s} className="w-4 h-4 fill-primary text-primary" />)}
                </div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{tr.reviews_rating_label}</div>
              </div>
            </div>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tr.reviews.map((r, i) => (
              <motion.div
                key={i}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fade}
                className="relative border border-border bg-card p-8 flex flex-col hover:border-primary/50 transition-colors"
                data-testid={`review-${i}`}
              >
                <Quote className="w-8 h-8 text-primary/25 absolute top-6 right-6" />
                <div className="flex gap-0.5">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className={`w-4 h-4 ${s < r.rating ? "fill-primary text-primary" : "text-border"}`} />
                  ))}
                </div>
                <p className="mt-5 text-sm leading-relaxed text-foreground/90 flex-1">“{r.text}”</p>
                <div className="mt-6 pt-6 border-t border-border flex items-center gap-3">
                  <span className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-primary/10 text-primary font-anton text-lg">{initials(r.name)}</span>
                  <div>
                    <div className="text-sm font-semibold">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EMPLOYERS */}
      <section id="employers" className="relative py-24 md:py-32 bg-[#08281d] text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
            <span className="inline-block text-[#5fe6a0] text-xs tracking-[0.25em] border border-[#5fe6a0]/40 px-4 py-2 mb-6">{tr.emp_tag}</span>
            <h2 className="font-anton text-4xl md:text-6xl uppercase leading-[0.95]">{tr.emp_title}</h2>
            <p className="mt-5 text-white/75 max-w-lg leading-relaxed">{tr.emp_sub}</p>
            <a href={`mailto:${CONTACT.email}`} className="mt-8 inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors" data-testid="employers-cta">
              <Building2 className="w-4 h-4" /> {tr.emp_cta}
            </a>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="space-y-px bg-white/10 border border-white/15">
            {tr.emp_points.map((p, i) => (
              <div key={i} className="flex items-center gap-4 bg-[#08281d] p-6" data-testid={`emp-point-${i}`}>
                <span className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-[#3ee08a]/20 text-[#5fe6a0]">
                  <Check className="w-5 h-5" />
                </span>
                <span className="text-sm md:text-base">{p}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 bg-background">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="text-center">
            <span className="inline-block text-primary text-xs tracking-[0.25em] border border-primary/30 px-4 py-2 mb-6">{tr.faq_tag}</span>
            <h2 className="font-anton text-5xl md:text-6xl uppercase">{tr.faq_title}</h2>
            <p className="mt-4 text-muted-foreground">{tr.faq_sub}</p>
          </motion.div>
          <div className="mt-12 border-t border-border">
            {tr.faq.map((f, i) => (
              <div key={i} className="border-b border-border" data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-5 text-left hover:text-primary transition-colors">
                  <span className="font-semibold text-base md:text-lg">{f.q}</span>
                  {openFaq === i ? <Minus className="w-5 h-5 flex-shrink-0 text-primary" /> : <Plus className="w-5 h-5 flex-shrink-0" />}
                </button>
                {openFaq === i && <p className="pb-5 text-sm text-muted-foreground leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="text-center">
            <h2 className="font-anton text-5xl md:text-6xl uppercase">{tr.form_title}</h2>
            <p className="mt-4 text-muted-foreground">{tr.form_sub}</p>
          </motion.div>
          <form onSubmit={submit} className="mt-12 border border-border bg-card p-8 md:p-10 space-y-5" data-testid="application-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{tr.f_name}</label>
                <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="form-name" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{tr.f_phone}</label>
                <input required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="form-phone" />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{tr.f_email}</label>
              <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="form-email" />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{tr.f_license}</label>
                <select required value={form.license_type} onChange={set("license_type")} className={inputCls} data-testid="form-license">
                  <option value="" disabled>—</option>
                  {tr.lic_opts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{tr.f_exp}</label>
                <select required value={form.experience} onChange={set("experience")} className={inputCls} data-testid="form-experience">
                  <option value="" disabled>—</option>
                  {tr.exp_opts.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{tr.f_avail}</label>
              <select value={form.availability} onChange={set("availability")} className={inputCls} data-testid="form-availability">
                <option value="" disabled>—</option>
                {tr.avail_opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">{tr.f_certs}</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="form-certificates">
                {tr.cert_opts.map((c) => {
                  const active = form.certificates.includes(c);
                  return (
                    <button
                      type="button"
                      key={c}
                      onClick={() => toggleCert(c)}
                      data-testid={`cert-${c}`}
                      className={`flex items-center gap-3 border px-3 py-2.5 text-left text-sm transition-colors ${active ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/60"}`}
                    >
                      <span className={`flex-shrink-0 w-5 h-5 flex items-center justify-center border ${active ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
                        {active && <Check className="w-3.5 h-3.5" />}
                      </span>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{tr.f_msg}</label>
              <textarea rows={4} value={form.message} onChange={set("message")} className={inputCls} data-testid="form-message" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">{tr.f_cv}</label>
              <label className="flex items-center gap-3 border border-dashed border-border px-4 py-3 text-sm cursor-pointer hover:border-primary transition-colors" data-testid="form-cv-label">
                {form.cv_filename ? <FileText className="w-5 h-5 text-primary" /> : <Upload className="w-5 h-5 text-muted-foreground" />}
                <span className={form.cv_filename ? "text-foreground" : "text-muted-foreground"}>
                  {uploading ? "Uploaden..." : form.cv_filename || "PDF, JPG of DOC (max 10MB)"}
                </span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={uploadCv} className="hidden" data-testid="form-cv-input" />
              </label>
            </div>
            <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors disabled:opacity-60" data-testid="form-submit">
              {sending ? tr.f_sending : tr.f_submit}
              {!sending && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        </div>
      </section>

      {/* CONTACT */}
      <section className="border-t border-white/10 bg-[#08281d] text-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-anton text-4xl md:text-5xl uppercase">{tr.contact_title}</h2>
            <p className="mt-4 text-white/70 max-w-md">{tr.contact_sub}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 border border-white/15 p-6 hover:border-[#3ee08a] transition-colors group" data-testid="contact-whatsapp">
              <MessageCircle className="w-8 h-8 text-[#3ee08a]" strokeWidth={1.5} />
              <div><div className="text-xs uppercase tracking-widest text-white/50">WhatsApp</div><div className="text-sm mt-1">{CONTACT.phone}</div></div>
            </a>
            <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-4 border border-white/15 p-6 hover:border-[#3ee08a] transition-colors" data-testid="contact-phone">
              <Phone className="w-8 h-8 text-[#3ee08a]" strokeWidth={1.5} />
              <div><div className="text-xs uppercase tracking-widest text-white/50">Tel</div><div className="text-sm mt-1">{CONTACT.phone}</div></div>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="sm:col-span-2 flex items-center gap-4 border border-white/15 p-6 hover:border-[#3ee08a] transition-colors" data-testid="contact-email">
              <Mail className="w-8 h-8 text-[#3ee08a]" strokeWidth={1.5} />
              <div><div className="text-xs uppercase tracking-widest text-white/50">E-mail</div><div className="text-sm mt-1">{CONTACT.email}</div></div>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border bg-card py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col items-center text-center gap-4">
          <img src={LOGO} alt="Northline Recruitment" className="h-20 w-auto" />
          <p className="text-primary text-sm md:text-base tracking-wide font-medium">{tr.footer_tag}</p>
          <p className="text-muted-foreground text-sm max-w-md">{tr.footer_about}</p>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mt-2">{tr.footer_slogan}</p>
          <div className="w-16 h-px bg-border mt-4" />
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Northline Recruitment — {CONTACT.phone} — {CONTACT.email}</p>
          <Link to="/admin" className="text-xs text-muted-foreground/70 hover:text-primary transition-colors mt-1" data-testid="footer-admin-link">Admin</Link>
          <button onClick={() => setShowPrivacy(true)} className="text-xs text-muted-foreground/70 hover:text-primary transition-colors" data-testid="footer-privacy-link">{tr.privacy_link}</button>
        </div>
      </footer>

      {/* FLOATING WHATSAPP */}
      <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:scale-105 transition-transform" data-testid="float-whatsapp" aria-label="WhatsApp">
        <MessageCircle className="w-7 h-7" />
      </a>

      {/* COOKIE CONSENT */}
      {!cookieOk && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#08281d] text-white border-t border-white/10" data-testid="cookie-banner">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex flex-col sm:flex-row items-center gap-4 justify-between">
            <p className="text-xs text-white/80 text-center sm:text-left">
              {tr.cookie_text}{" "}
              <button onClick={() => setShowPrivacy(true)} className="underline hover:text-[#3ee08a]">{tr.privacy_link}</button>
            </p>
            <button onClick={() => { setCookieOk(true); localStorage.setItem("nl_cookie", "1"); }} className="flex-shrink-0 bg-primary text-primary-foreground px-6 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors" data-testid="cookie-accept">
              {tr.cookie_accept}
            </button>
          </div>
        </div>
      )}

      {/* PRIVACY MODAL */}
      {showPrivacy && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70" onClick={() => setShowPrivacy(false)} data-testid="privacy-modal">
          <div className="bg-card border border-border max-w-lg w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowPrivacy(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground" data-testid="privacy-close"><X className="w-5 h-5" /></button>
            <h3 className="font-anton text-2xl uppercase">{tr.privacy_title}</h3>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{tr.privacy_body}</p>
          </div>
        </div>
      )}
    </div>
  );
}
